import { type TrackFields } from '@/track/trackFieldsFrag'
import type { Maybe } from '@amazon/vinyl-util'
import { createShortUid, logError } from '@amazon/vinyl-util'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import { parseMrn } from '@/util/mrn'
import { getLargestImage } from '@/util/imageUtil'
import { ctx } from '@/util/context'
import { getAuth } from '@/auth/authRef'

const { target } = ctx('trackGqlUtil')

export function trackToPlaylistEntry(track: TrackFields): PlaylistEntry | null {
    if (!track.uri) return null
    const artistName = track.contributingArtists?.concatenatedName ?? ''
    const mrn = parseMrn(track.uri)

    const image = getLargestImage(track.images, 'PRIMARY')

    if (!track.id) {
        logError(target, 'track missing id')
    }

    // Anonymous users can only play 30-second previews; signed-in users get
    // the full asin track. The player resolves these load types differently.
    const isSignedIn = getAuth().isSignedIn()

    return {
        id: track.id ?? createShortUid(),
        entity: track,
        albumTitle: track.album?.shortTitle ?? track.album?.title ?? '',
        artistName,
        trackId: track.id ?? null,
        albumId: track.album?.id ?? null,
        artistId: track.contributingArtists?.edges?.[0]?.node?.id ?? null,
        duration: track.duration ?? 0,
        image: image,
        title: track.title ?? '',
        trackLoadOptions: isSignedIn
            ? {
                  type: 'asin',
                  uri: `asin://${mrn.identifier}`,
              }
            : {
                  // Anonymous: sample-only. The uri scheme switches to
                  // `sample://` so vinyl-amzn dispatches the request
                  // to the media-central preview endpoint instead of
                  // the catalog endpoint that requires sign-in.
                  type: 'sample',
                  uri: `sample://${mrn.identifier}`,
              },
    }
}

export function getTrackArtistStr(track: Maybe<TrackFields>): string {
    return track?.contributingArtists?.concatenatedName ?? ''
}

export function getTrackAlbumTitle(track: Maybe<TrackFields>): string {
    // shortTitle is gated on $isAuthenticated — anonymous responses
    // omit it entirely. Fall back to the full title so the album
    // cell still reads on guest-mode pages (popular/songs, etc).
    return track?.album?.shortTitle ?? track?.album?.title ?? ''
}
